import {BrickContext} from 'olympe';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {
    Box, Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent, DialogTitle,
    FormControl,
    IconButton, InputAdornment, LinearProgress,
    Select, TextField, ThemeProvider, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {accountTypeOptions, ezprOrange} from '../../_helpers';
import {MasterDataDropdown} from '../utils/MasterDataDropdown.jsx';
import MenuItem from '@mui/material/MenuItem';
import {LAMBDA_NAMES, TRANSPORT_OPTIONS_ENUM} from '../../../utils/constants';
import {addNBusinessDays} from '../../../utils/date';
import {Material} from '../../../models/Material';
import {checkLabelValueObject, isLabelValue, isLabelValueEmpty} from '../../../utils/checkObjects';
import {checkCompanyCodeLambda, getL3CategoryLambda, getMaterialTypeLambda} from '../../../utils/functionSignatures';
import UserController from '../../../controllers/UserController';
import {EZPRTheme} from '../../_theme';
import {ControlledEZPRSelect} from '../utils/ControlledEZPRSelect.jsx';
import LoadingButton from '@mui/lab/LoadingButton';
import {DataGrid} from '@mui/x-data-grid';
import PSLController from '../../../controllers/PSLController';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {Account} from '../../../models/financial_infos/Account';
import {normalizeCode} from '../../../utils/helpers';
import {ItemCodeDescriptionDropdown} from '../../dialogs/dyn/ItemCodeDescriptionDropdown.jsx';
import {L3CategoryDropdown} from '../../dialogs/dyn/L3CategoryDropdown.jsx';
import {convertSourceToLabelValue} from '../../../utils/sourceMappings';

/**
 * This component display a popup for adding a material to a PR
 * @param {AddMaterialPopup.propTypes} props
 * @return {JSX.Element}
 */
export function AddMaterialPopup(props) {
    const useRequestStore = requestStores.get(props.prTag);
    const companyCode = useRequestStore((state) => state.companyCode);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const defaultPlant = useRequestStore((state) => state.defaultPlant);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const vendorName = useRequestStore((state) => state.vendorName);
    const defaultStorageLocation = useRequestStore((state) => state.defaultStorageLocation);
    const defaultDeliveryAddress = useRequestStore((state) => state.defaultDeliveryAddress);
    const accounts = useRequestStore((state) => state.accounts);
    const accountDetailsByMaterial = useRequestStore((state) => state.accountDetailsByMaterial);
    const areAccountSplitByMaterial = useRequestStore((state) => state.areAccountSplitByMaterial);
    const addAccountDetailsByMaterials = useRequestStore((state) => state.addAccountDetailsByMaterials);
    const EPRId = useRequestStore((state) => state.EPRId);
    const wayOfBuying = useRequestStore((state) => state.wayOfBuying);
    const [materials, updateAllMaterials] = useRequestStore((state) => [state.materials, state.updateAllMaterials]);

    const accountSource = areAccountSplitByMaterial && accountDetailsByMaterial?.length > 0 && accountDetailsByMaterial[0]?.accounts?.length > 0 ?
        accountDetailsByMaterial[0].accounts[0] :
        accounts?.length > 0 ? accounts[0] : {};

    const getMasterData = props.context.get(LAMBDA_NAMES.GET_MASTER_DATA);
    const getPSLRequest = props.context.get(LAMBDA_NAMES.GET_PSL_REQUEST);
    const getL3Category = props.context.get(LAMBDA_NAMES.GET_L3_CATEGORY);
    const getMaterialType = props.context.get(LAMBDA_NAMES.GET_MATERIAL_TYPE);
    const checkCompanyCode = props.context.get(LAMBDA_NAMES.CHECK_COMPANY_CODE);
    const getItemCodeDesc = props.context.get(LAMBDA_NAMES.OG_ITEM_CODE_DESC);
    const mlGetL3Category = props.context.get(LAMBDA_NAMES.ML_GET_L3_CATEGORY);
    const getAllL3Categories = props.context.get(LAMBDA_NAMES.OG_GET_ALL_L3_CATEGORIES);

    const [userId, setUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [itemCode, setItemCode] = useState({label: '', value: ''});
    const [itemCodeSearchProp, setItemCodeSearchProp] = useState('code');
    const [isDYNEnabled, setIsDYNEnabled] = useState(true);

    const [materialTypeLoading, setMaterialTypeLoading] = useState(false);
    const [l3CategoryLoading, setL3CategoryLoading] = useState(false);
    const [l3Category, setL3Category] = useState('');
    const [materialType, setMaterialType] = useState('');
    const [baseUnit, setBaseUnit] = useState('');
    const [plant, setPlant] = useState(checkLabelValueObject(defaultPlant));
    const [purchaseOrg, setPurchaseOrg] = useState(checkLabelValueObject(purchaseOrganization));
    const [itemDescription, setItemDescription] = useState('');
    const [itemCodeError, setItemCodeError] = useState(false);
    const [itemDescriptionError, setItemDescriptionError] = useState(false);
    const [moreViewOpen, setMoreViewOpen] = useState(false);
    const [accountType, setAccountType] = useState(checkLabelValueObject(accountSource?.accountType));
    const [wbs, setWBS] = useState(checkLabelValueObject(accountSource?.wbs));
    const [costCenter, setCostCenter] = useState(checkLabelValueObject(accountSource?.costCenter));
    const [profitCenter, setProfitCenter] = useState(checkLabelValueObject(accountSource?.profitCenter));
    const [glAccount, setGlAccount] = useState(checkLabelValueObject(accountSource?.glAccount));
    const [taxCode, setTaxCode] = useState(checkLabelValueObject(materials[0]?.taxCode));
    const [totalPSLEntries, setTotalPSLEntries] = useState(0);

    const [chips, setChips] = useState({
        vendor: true,
        supplierUndefined: true,
    });
    const [suppliers, setSuppliers] = useState([]);
    const [selectedMaterials, setSelectedMaterials] = useState({});

    const [pageState, setPageState] = useState({
        total: suppliers.length,
        page: 0,
        pageSize: 10,
    });

    const columns = [
        {field: 'pslId', headerName: 'PSL ID', flex: 1.2, headerClassName: 'data-grid-header'},
        {field: 'materialId', headerName: 'PSL Material ID', flex: 1.2, headerClassName: 'data-grid-header'},
        {field: 'supplier', headerName: 'Supplier', flex: 2.3, headerClassName: 'data-grid-header'},
        {field: 'material', headerName: 'Material', flex: 1, headerClassName: 'data-grid-header'},
        {field: 'materialDescription', headerName: 'Material Description', flex: 2.3, headerClassName: 'data-grid-header'},
        {field: 'status', headerName: 'Status', flex: 1, headerClassName: 'data-grid-header'},
        {field: 'price', headerName: 'Price', flex: 1, headerClassName: 'data-grid-header'},
        {field: 'leadTimes', headerName: 'Lead Times', flex: 1, headerClassName: 'data-grid-header'},
        {
            field: 'strategicBuyer',
            headerName: 'Strategic Buyer',
            flex: 1,
            headerClassName: 'data-grid-header',
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%'}}>
                    {addAvatar(params.value)}
                </Box>
            ),
        },
    ];

    // This useEffect fetches the user ID on mount
    useEffect(() => {
        const userID = UserController.getCurrentUserName('EZPR-USER');
        setUserId(userID);
    }, []);

    // This useEffect fetches the PSL data on item code change
    useEffect(() => {
        setLoading(true);
        setSuppliers([]);
        setPageState((old) => ({...old, page: 0}));
        if (itemCode && itemCode.value && itemCode.value.length > 0) {
            queryPSL();
        } else {
            setLoading(false);
            setTotalPSLEntries(0);
        }
    }, [itemCode, chips]);

    // This useEffect clear the selected supplier if purchase org or company code is modified
    useEffect(() => {
        setSuppliers([]);
        setItemCode({label: '', value: ''});
    }, [purchaseOrg, plant]);

    // This useEffect checks if DYN is enabled for the company code
    useEffect(() => {
        checkCompanyCodeLambda(
            props.context,
            checkCompanyCode,
            companyCode.value,
            (result) => {
                const parsedResult = JSON.parse(result);
                setIsDYNEnabled(parsedResult);
            },
            () => {
                setIsDYNEnabled(true);
            },
        );
    }, []);

    useEffect(() => {
        const isItemCodeEmpty = !itemCode?.value;
        const isItemDescriptionEmpty = !itemDescription?.trim();

        if (isItemCodeEmpty) {
            setItemCodeError(true);
            setItemDescriptionError(false);
        } else {
            if (isItemDescriptionEmpty) {
                setItemCodeError(false);
                setItemDescriptionError(true);
            } else {
                setItemCodeError(false);
                setItemDescriptionError(false);
            }
        }
    }, [itemCode, itemDescription]);

    const queryPSL = () => {
        PSLController.getPSLRequest(
            props.context,
            getPSLRequest,
            itemCode.value,
            '0',
            '10000',
            companyCode?.value,
        )
            .then((result) => {
                const pslData = result.response?.data || [];
                const relevantPSLData = pslData.filter((supplier) => {
                    const normalizedChildSupplierCode = normalizeCode(supplier.childSupplierCode);
                    return normalizedChildSupplierCode === normalizeCode(vendorCode) || normalizedChildSupplierCode.startsWith('9');
                });
                setTotalPSLEntries(relevantPSLData.length);

                const filteredSuppliers = result.response?.data?.filter((supplier) => {
                    const normalizedChildSupplierCode = normalizeCode(supplier.childSupplierCode);

                    if (!chips.vendor && !chips.supplierUndefined) {
                        return false;
                    }

                    const isVendorMatch = normalizedChildSupplierCode === normalizeCode(vendorCode);
                    const isSupplierUndefined = normalizedChildSupplierCode.startsWith('9');

                    if (chips.vendor && chips.supplierUndefined) {
                        return isVendorMatch || isSupplierUndefined;
                    }

                    if (chips.vendor) {
                        return isVendorMatch;
                    }

                    if (chips.supplierUndefined) {
                        return isSupplierUndefined;
                    }

                    return false;
                }) || [];

                setSuppliers(filteredSuppliers);
                setPageState((old) => ({
                    ...old,
                    total: filteredSuppliers.length,
                }));
            })
            .catch((error) => {
                console.error('Error fetching PSL data:', error);
                setSuppliers([]);
                setPageState((old) => ({...old, total: 0}));
                setTotalPSLEntries(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleClickOnAdd = () => {
        const maxCurrentId = materials.length > 0 ? Math.max(...materials.map((m) => m.id)) : 0;

        let newMaterials;

        if (Object.keys(selectedMaterials).length > 0) {
            newMaterials = Object.keys(selectedMaterials).map((uniqueId, index) => {
                const [actualIndex] = uniqueId.split('-');
                const supplierSelected = suppliers[parseInt(actualIndex, 10)];

                if (!supplierSelected) {
                    console.error(`Supplier with unique ID ${uniqueId} not found.`);
                    return null;
                }

                const material = new Material(
                    maxCurrentId + index + 1,
                    itemCode.label,
                    supplierSelected?.shortDescription || '',
                    wayOfBuying.currentValue.value !== 'FO' ? '' : '1',
                    supplierSelected?.unitOfMeasure || '',
                    supplierSelected?.unitOfMeasure || '',
                    supplierSelected?.unitPrice || '',
                    materials[0]?.currency || '',
                    supplierSelected?.pricePer || '',
                    taxCode ? taxCode : {label: '', value: ''},
                    '',
                    '',
                    l3Category,
                    addNBusinessDays(new Date(), supplierSelected?.supplierLeadTime || 5).toUTCString(),
                    plant,
                    defaultStorageLocation,
                    defaultDeliveryAddress,
                    '',
                    false,
                    false,
                    supplierSelected?.supplierPartId || '',
                    supplierSelected?.supplierPartAuxiliaryId || '',
                    false,
                    '',
                    '',
                    '',
                    materialType,
                    supplierSelected?.statusName || 'Blank',
                    supplierSelected?.pslId,
                    TRANSPORT_OPTIONS_ENUM['No transport cost added'],
                    [],
                    supplierSelected?.materialId || '',
                    '',
                    '0',
                    convertSourceToLabelValue('PSL'),
                );

                return material.getMaterial();
            }).filter(Boolean);
        } else {
            const material = new Material(
                maxCurrentId + 1,
                itemCode.label,
                itemDescription || '',
                wayOfBuying.currentValue.value !== 'FO' ? '' : '1',
                baseUnit,
                baseUnit,
                '',
                materials[0]?.currency || '',
                '',
                taxCode ? taxCode : {label: '', value: ''},
                '',
                '',
                l3Category,
                addNBusinessDays(new Date(), 5).toUTCString(),
                plant,
                defaultStorageLocation,
                defaultDeliveryAddress,
                '',
                false,
                false,
                '',
                '',
                false,
                '',
                '',
                '',
                materialType,
                'Blank',
                '',
                TRANSPORT_OPTIONS_ENUM['No transport cost added'],
                [],
                '',
                '',
                '0',
                convertSourceToLabelValue('NCT'),
            );

            newMaterials = [material.getMaterial()];
        }

        if (areAccountSplitByMaterial) {
            const newAccountDetailsEntries = newMaterials.map((material) => ({
                material_id: material.id,
                accounts: [
                    new Account(
                        {label: '', value: ''},
                        {label: '', value: ''},
                        {label: '', value: ''},
                        {label: '', value: ''},
                        {label: '', value: ''},
                        {label: '', value: ''},
                        100,
                        'percentage',
                        '',
                        '',
                        false,
                    ).getAccount(),
                ],
            }));

            addAccountDetailsByMaterials(newAccountDetailsEntries);
        }

        const updatedMaterials = [...materials, ...newMaterials];
        updateAllMaterials(updatedMaterials);
        props.onClose();
    };

    const handleItemCodeChange = (newValue) => {
        setItemCode(newValue);

        if (!newValue || !newValue.value) {
            setMaterialType('');
            setItemDescription('');
            setL3Category('');
            return;
        }

        setItemDescription(newValue.label.split(' - ')[1]);

        const paddedValue = newValue.value.padStart(18, '0');

        if (newValue && !isLabelValueEmpty(newValue)) {
            // Fetch the L3 Category
            setL3CategoryLoading(true);
            getL3CategoryLambda(
                props.context,
                getL3Category,
                paddedValue,
                (result) => {
                    try {
                        const parsedResult = JSON.parse(result);
                        if (parsedResult.length > 0) {
                            const newL3 = parsedResult[0].matkl + ' - ' + parsedResult[0].wgbez;
                            setL3Category(newL3);
                        } else {
                            if (!l3Category) {
                                setL3Category('');
                                setItemCode({label: '', value: ''});
                                setItemDescription('');
                                setMaterialType('');
                            }
                        }
                    } catch (error) {
                        console.error('Error parsing L3 Category result:', error);
                        if (!l3Category) {
                            setL3Category('');
                        }
                    }
                    setL3CategoryLoading(false);
                },
                () => {
                    if (!l3Category) {
                        setL3Category('');
                    }
                    setL3CategoryLoading(false);
                },
            );
            // Fetch the material type
            setMaterialTypeLoading(true);
            getMaterialTypeLambda(
                props.context,
                getMaterialType,
                newValue.value,
            )
                .then((result) => {
                    const parsedResult = JSON.parse(result[0]);
                    setMaterialType(parsedResult[0]?.mtart || '');
                    setBaseUnit(parsedResult[0]?.meins || '');
                })
                .finally(() => setMaterialTypeLoading(false));
        } else if (isLabelValueEmpty(newValue)) {
            setL3Category('');
            setItemCode({label: '', value: ''});
            setItemDescription('');
            setMaterialType('');
        }
    };

    const handleL3CategoryChange = (option) => {
        const newL3 = option?.value || '';
        if (l3Category && newL3 !== l3Category) {
            setItemCode({label: '', value: ''});
            setItemDescription('');
            setMaterialType('');
        }
        setL3Category(newL3);
    };

    const handleChangeItemCodeSearchProp = (newValue) => {
        setItemCodeSearchProp(newValue.target.value);
    };

    const handlePlantChange = (newValue) => {
        setPlant(newValue);
    };

    const handlePurchaseOrgChange = (newValue) => {
        setPurchaseOrg(newValue);
    };

    const handleItemDescriptionChange = (event) => {
        setItemDescription(event.target.value);
    };

    const handleAccountTypeChange = (_, newValue) => {
        if (isLabelValue(newValue)) {
            setAccountType(newValue);
        }
    };

    const handleWBSChange = (newValue) => {
        setWBS(newValue);
    };

    const handleCostCenterChange = (newValue) => {
        setCostCenter(newValue);
    };

    const handleProfitCenterChange = (newValue) => {
        setProfitCenter(newValue);
    };

    const handleGlAccountChange = (newValue) => {
        setGlAccount(newValue);
    };

    const handleTaxCodeChange = (newValue) => {
        setTaxCode(newValue);
    };

    const handleChipToggle = (chipKey) => {
        setChips((prevChips) => ({
            ...prevChips,
            [chipKey]: !prevChips[chipKey],
        }));
    };

    const handlePageChange = (newPage) => {
        setPageState((old) => ({
            ...old,
            page: newPage,
        }));
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageState((old) => ({
            ...old,
            pageSize: newPageSize,
            page: 0,
        }));
    };

    const onSelectionModelChange = (newSelection) => {
        setSelectedMaterials((prevSelected) => {
            const currentPageMaterials = suppliers.slice(
                pageState.page * pageState.pageSize,
                (pageState.page + 1) * pageState.pageSize,
            ).map((supplier, index) => `${pageState.page * pageState.pageSize + index}-${supplier.pslId}`);

            const updatedSelected = {...prevSelected};

            currentPageMaterials.forEach((id) => {
                if (!newSelection.includes(id)) {
                    delete updatedSelected[id];
                }
            });

            newSelection.forEach((id) => {
                updatedSelected[id] = true;
            });

            if (JSON.stringify(updatedSelected) !== JSON.stringify(prevSelected)) {
                return updatedSelected;
            } else {
                return prevSelected;
            }
        });
    };

    const CustomNoDataOverlay = React.memo(() => {
        if (itemCode?.value) {
            if (totalPSLEntries > 0 && suppliers.length === 0) {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            textAlign: 'center',
                            padding: '0 1rem',
                        }}
                    >
                        No PSL supplier found for this selection. Review your filters and select one of the available options.
                    </div>
                );
            } else if (totalPSLEntries === 0) {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            textAlign: 'center',
                            padding: '0 1rem',
                        }}
                    >
                        No PSL supplier found.
                    </div>
                );
            }
        }
        return <div style={{height: '100%'}}></div>;
    });

    const addAvatar = (email) => {
        if (!email) {
            return null;
        }

        const emailName = email.split('@')[0];
        const nameParts = emailName.split('.');

        const initials = nameParts.length > 1 ?
            `${nameParts[0][0].toUpperCase()}${nameParts[1][0].toUpperCase()}` :
            `${nameParts[0][0].toUpperCase()}`;

        return (
            <Tooltip title={email} placement={'top'} arrow>
                <Avatar sx={{backgroundColor: ezprOrange, fontSize: '18px', m: 'auto'}}>
                    {initials}
                </Avatar>
            </Tooltip>
        );
    };

    const isButtonDisabled = !itemCode.value ||
        (isDYNEnabled && totalPSLEntries > 0 && Object.keys(selectedMaterials).length === 0) ||
        (!isDYNEnabled && Object.keys(selectedMaterials).length === 0);

    const showDYNDisabledTooltip =
        !isDYNEnabled && Object.keys(selectedMaterials).length === 0;

    return (
        <ThemeProvider theme={EZPRTheme}>
            <Dialog open={props.open} onClose={props.onClose} fullWidth={true} maxWidth={'xl'}>
                <div className={'pr-popup-header'}>
                    <DialogTitle>What do you need?</DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={props.onClose}
                        sx={{
                            color: ezprOrange,
                        }}>
                        <CloseIcon
                            sx={{
                                width: '50px',
                                height: '50px',
                            }}
                        />
                    </IconButton>
                </div>
                <DialogContent className={'popup-dyn-container'}>
                    <div className={'popup-dyn-content'}>
                        <div>Company code: *</div>
                        <MasterDataDropdown
                            error={!companyCode.value}
                            property={'Company Code'}
                            selectedValue={companyCode}
                            context={props.context}
                            getMasterData={getMasterData}
                            disabled={true}
                            filterQueryWithSearch={true}
                            userId={userId}
                        />
                        <div>Purchase organization: *</div>
                        <MasterDataDropdown
                            error={!purchaseOrganization.value}
                            property={'Purchase Organization'}
                            selectedValue={purchaseOrg}
                            context={props.context}
                            getMasterData={getMasterData}
                            onChange={handlePurchaseOrgChange}
                            filterQueryWithSearch={true}
                            userId={userId}
                            disabled={true}
                        />
                        <div>Plant: *</div>
                        <MasterDataDropdown
                            error={!plant.value}
                            property={'Plant'}
                            selectedValue={plant}
                            context={props.context}
                            getMasterData={getMasterData}
                            onChange={handlePlantChange}
                            selectedCompanyCode={companyCode}
                            filterQueryWithSearch={true}
                            userId={userId}
                        />
                        <div>L3 Category: *</div>
                        <L3CategoryDropdown
                            context={props.context}
                            selectedValue={l3Category ? {label: l3Category, value: l3Category} : null}
                            onChange={handleL3CategoryChange}
                            mlGetL3Category={mlGetL3Category}
                            companyCode={companyCode.value}
                            purchaseOrg={purchaseOrg.value}
                            plant={plant.value}
                            vendorCode={vendorCode}
                            costCenter={costCenter.value}
                            glAccount={glAccount.value}
                            getAllL3Categories={getAllL3Categories}
                        />
                        <div>Item code: *</div>
                        <div className={'popup-dyn-item-code'}>
                            <div className={'popup-dyn-item-code-text-field'}>
                                <ItemCodeDescriptionDropdown
                                    context={props.context}
                                    l3Category={l3Category}
                                    plant={plant}
                                    getItemCodeDesc={getItemCodeDesc}
                                    itemCodeSearchProp={itemCodeSearchProp}
                                    onChange={handleItemCodeChange}
                                    selectedValue={itemCode}
                                    error={itemCodeError}
                                    getMasterData={getMasterData}
                                    userId={userId}
                                    enableSuggestions
                                />
                            </div>
                            <div className={'popup-dyn-item-code-select'}>
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={itemCodeSearchProp}
                                        onChange={handleChangeItemCodeSearchProp}
                                    >
                                        <MenuItem value={'code'}>By code</MenuItem>
                                        <MenuItem value={'name'}>By name</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div>Item Description: *</div>
                        <TextField
                            variant={'standard'}
                            size={'small'}
                            fullWidth
                            value={itemDescription}
                            onChange={handleItemDescriptionChange}
                            error={itemDescriptionError}
                        />
                        <div>Material type:</div>
                        <TextField
                            value={materialType}
                            InputProps={{
                                startAdornment: (materialTypeLoading &&
                                    <InputAdornment position="start">
                                        <CircularProgress size={20}/>
                                    </InputAdornment>
                                ),
                            }}
                            variant={'standard'}
                            size={'small'}
                            disabled
                        />
                    </div>
                    <Typography variant="body2" color="textSecondary" sx={{
                        textAlign: 'center',
                        flex: 1,
                        marginTop: '1rem',
                        marginLeft: '2rem',
                        fontWeight: '500',
                    }}>
                        Note: Only active materials (Z3 status) can be displayed
                    </Typography>
                    <div style={{
                        display: 'flex',
                        gap: '5px',
                        alignItems: 'center',
                    }}>
                        <div onClick={() => setMoreViewOpen(!moreViewOpen)} className={'popup-dyn-more-info-text'}>
                            More information
                        </div>
                        <div onClick={() => setMoreViewOpen(!moreViewOpen)}
                            className={moreViewOpen ? 'popup-dyn-arrow-up' : 'popup-dyn-arrow-down'}
                        />
                    </div>
                    {moreViewOpen && <div className={'popup-dyn-horizontal-separator'}/>}
                    {moreViewOpen && (
                        <div className={'popup-dyn-more-info-content'}>
                            <div>Account type:</div>
                            <ControlledEZPRSelect
                                handleChange={handleAccountTypeChange}
                                options={accountTypeOptions}
                                value={accountType}
                                readOnly={true}
                            />
                            {
                                accountType.value === 'P' || accountType.value === 'S' ?
                                    <>
                                        <div>WBS Project</div>
                                        <MasterDataDropdown
                                            property={'WBS'} // with selectedAccountType being 'P' => WBS Project
                                            selectedValue={wbs}
                                            selectedCompanyCode={companyCode}
                                            onChange={handleWBSChange}
                                            context={props.context}
                                            getMasterData={getMasterData}
                                            filterQueryWithSearch={true}
                                            autoSelect
                                            userId={userId}
                                            disabled={true}
                                        />
                                    </> : undefined
                            }
                            {
                                accountType.value === 'K' || accountType.value === 'S' ?
                                    <>
                                        <div>Cost Center</div>
                                        <MasterDataDropdown
                                            property={'Cost Center'} // with selectedAccountType being 'K' => Cost Center
                                            selectedValue={costCenter}
                                            onChange={handleCostCenterChange}
                                            context={props.context}
                                            getMasterData={getMasterData}
                                            selectedCompanyCode={companyCode}
                                            filterQueryWithSearch={true}
                                            userId={userId}
                                            disabled={true}
                                        />
                                    </> : undefined
                            }
                            {
                                accountType.value === '2' ?
                                    <>
                                        <div>Profit Center</div>
                                        <MasterDataDropdown
                                            property={'Profit Center'}
                                            selectedValue={profitCenter}
                                            onChange={handleProfitCenterChange}
                                            context={props.context}
                                            getMasterData={getMasterData}
                                            selectedCompanyCode={companyCode}
                                            filterQueryWithSearch={true}
                                            userId={userId}
                                            disabled={true}
                                        />
                                    </> : undefined
                            }
                            {
                                !!accountType.value ?
                                    <>
                                        <div>G/L number:</div>
                                        <MasterDataDropdown
                                            property={'GL Account'}
                                            selectedValue={glAccount}
                                            context={props.context}
                                            getMasterData={getMasterData}
                                            onChange={handleGlAccountChange}
                                            filterQueryWithSearch={true}
                                            userId={userId}
                                            disabled={true}
                                        />
                                    </> : undefined
                            }
                            <div>Item tax code:</div>
                            <MasterDataDropdown
                                property={'Tax Code'}
                                selectedValue={taxCode}
                                onChange={handleTaxCodeChange}
                                context={props.context}
                                getMasterData={getMasterData}
                                filterQueryWithSearch={true}
                                userId={userId}
                            />

                        </div>
                    )}
                    <div className={'popup-dyn-horizontal-separator'}/>
                    <Box
                        sx={{
                            overflowY: 'auto',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '5px',
                            minHeight: '2rem',
                        }}
                    >
                        <Chip
                            label={`${vendorCode} - ${vendorName}`}
                            icon={<CheckCircleIcon />}
                            onClick={() => handleChipToggle('vendor')}
                            sx={{
                                'margin': '4px',
                                'color': chips.vendor ? ezprOrange : 'grey',
                                'borderColor': chips.vendor ? ezprOrange : 'grey',
                                'borderWidth': '1px',
                                'borderStyle': 'solid',
                                '& .MuiChip-icon': {
                                    color: chips.vendor ? ezprOrange : 'grey',
                                },
                            }}
                            variant="outlined"
                        />
                        <Chip
                            label="999999 - Supplier Undefined"
                            icon={<CheckCircleIcon />}
                            onClick={() => handleChipToggle('supplierUndefined')}
                            sx={{
                                'margin': '4px',
                                'color': chips.supplierUndefined ? ezprOrange : 'grey',
                                'borderColor': chips.supplierUndefined ? ezprOrange : 'grey',
                                'borderWidth': '1px',
                                'borderStyle': 'solid',
                                '& .MuiChip-icon': {
                                    color: chips.supplierUndefined ? ezprOrange : 'grey',
                                },
                            }}
                            variant="outlined"
                        />
                    </Box>
                    <Box style={{display: 'flex', height: '29rem', width: '100%', marginTop: '16px'}}>
                        <div style={{flexGrow: 1}}>
                            <DataGrid
                                rows={
                                    suppliers.slice(
                                        pageState.page * pageState.pageSize,
                                        (pageState.page + 1) * pageState.pageSize,
                                    ).map((supplier, index) => ({
                                        id: `${pageState.page * pageState.pageSize + index}-${supplier.pslId}`,
                                        pslId: supplier.pslId,
                                        materialId: supplier.materialId,
                                        supplier: `${supplier.supplierCode} - ${supplier.supplierName}`,
                                        material: supplier.materialNumber,
                                        materialDescription: supplier.materialName,
                                        status: supplier.statusName,
                                        price: supplier.unitPrice,
                                        leadTimes: supplier.supplierLeadTime,
                                        strategicBuyer: supplier.strategicBuyerEmailAddress,
                                    }))
                                }
                                rowCount={pageState.total}
                                loading={loading || materialTypeLoading || l3CategoryLoading}
                                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                pagination
                                page={pageState.page}
                                pageSize={pageState.pageSize}
                                paginationMode="server"
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                columns={columns}
                                checkboxSelection
                                selectionModel={Object.keys(selectedMaterials)}
                                onSelectionModelChange={onSelectionModelChange}
                                components={{
                                    LoadingOverlay: LinearProgress,
                                    NoRowsOverlay: CustomNoDataOverlay,
                                    NoResultsOverlay: CustomNoDataOverlay,
                                }}
                                sx={{
                                    '& .data-grid-header': {
                                        backgroundColor: '#fafafa',
                                    },
                                    '& .MuiDataGrid-columnHeaderCheckbox': {
                                        backgroundColor: '#fafafa',
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        backgroundColor: '#fafafa',
                                    },
                                }}
                            />
                        </div>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Tooltip
                        title={
                            showDYNDisabledTooltip ? (
                                <>
                                    {'DYN is disabled for this company code.'}
                                    <br />
                                    {'Please select an available PSL supplier to add this material.'}
                                </>
                            ) : (
                                ''
                            )
                        }
                        placement="top"
                        arrow
                    >
                        <span>
                            <LoadingButton
                                sx={{
                                    'color': '#fff',
                                    'backgroundColor': ezprOrange,
                                    '&:hover': {backgroundColor: '#F2ae49'},
                                    ':disabled': {backgroundColor: 'rgba(0,0,0,0.26)'},
                                }}
                                variant={'outlined'}
                                onClick={handleClickOnAdd}
                                disabled={isButtonDisabled}
                                loading={loading}
                            >
                                <span>{`Add to EPR-${EPRId}`}</span>
                            </LoadingButton>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}

AddMaterialPopup.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
